import { Extension } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';

/**
 * Vorschläge im Texteditor – eine Bildsprache für alles, was Dispositio im Text erkennt (PLAN.md A127): mögliche Verweise, Zeitangaben,
 * Ortswechsel, Zustandsänderungen und die Perspektive sind gepunktet unterstrichen (Klasse `rt-suggest`, dazu die eigene der Art); Widersprüche (Zeitmarken,
 * die nicht mehr stimmen, §16d) sind rot (`rt-conflict`). Am Cursor – beim Prüfen an allen Stellen – stehen ✓ (annehmen) und ✕ (hier
 * nicht bzw. „ist Absicht“, gemerkt im Dokument), bei Widersprüchen dazwischen ein weiterer Ausweg; Alt+Enter bzw. Alt+Rücktaste tun
 * dasselbe am Cursor, Alt+↓/↑ springt zum nächsten bzw. vorigen. Jede Art bringt nur ihre Erkennung, ihre Darstellung und ihre Aktion
 * mit (`suggestExtension`). Beim ruhigen Schreiben (PF 57) blendet CSS die Farben aus; die Stelle, zu der man springt, zeigt sich trotzdem.
 */

export type SuggestKind = 'ref' | 'time' | 'place' | 'state' | 'pov' | 'split' | 'conflict';
/** annehmen, hier nicht (bzw. „ist Absicht“), ein weiterer Ausweg der Art (`SuggestSpec.other`) */
export type SuggestAct = 'accept' | 'ignore' | 'other';

/** Lage einer Stelle im Dokument */
export interface Located {
  from: number;
  to: number;
}

/** Was eine Art zur Laufzeit braucht (in einer Ref, damit der Editor nicht neu entsteht) */
export interface SuggestLive<H> {
  /** aus: keine Vorschläge dieser Art (etwa außerhalb des Drafts einer Szene) */
  on?: boolean;
  /** Prüfen: Knöpfe an allen Stellen */
  review: boolean;
  act: (act: SuggestAct, hit: H) => void;
  labels: Record<'accept' | 'ignore', string>;
}

export interface SuggestSpec<H extends Located, L extends SuggestLive<H>> {
  name: string;
  kind: SuggestKind;
  key: PluginKey<SuggestState<H>>;
  /** Treffer im Dokument (mit ihrer Lage), in Textreihenfolge; `state`: der ganze Zustand (andere Plugins davor) */
  hits: (doc: PMNode, live: L, state: EditorState) => H[];
  /** außer bei Textänderungen und `tr.setMeta(key, true)` auch hier neu berechnen */
  refresh?: (tr: Transaction) => boolean;
  /** Klasse und Attribute der Stelle; offene bekommen dazu `base` */
  mark: (hit: H, live: L) => Record<string, string> & { class: string };
  /** Klasse offener Stellen: `rt-suggest` (Vorschlag, gelb) bzw. `rt-conflict` (Widerspruch, rot) */
  base?: string;
  /** ✓ bzw. ✕ an dieser Stelle anders beschriftet bzw. ohne ✓ (null) */
  labels?: (hit: H, live: L) => { accept: string | null; ignore: string };
  /** ein weiterer Ausweg an der Stelle, als Knopf mit Text (etwa „Zeit aus dem Text“) */
  other?: (hit: H, live: L) => { label: string; title: string } | null;
  /** schon erledigt: bleibt markiert, ohne Knöpfe, zählt nicht */
  done?: (hit: H) => boolean;
  /** das Wort der Stelle (für die Beschriftung der Knöpfe) */
  word: (hit: H) => string;
  /** Kennung der Knöpfe im Test (`<testId>-accept`) */
  testId: string;
}

export interface SuggestState<H> {
  hits: H[];
  /** die offenen (ohne erledigte) */
  open: H[];
  deco: DecorationSet;
  /** Aktion dieses Editors */
  run: (act: SuggestAct, hit: H) => void;
}

/**
 * Die Erweiterungen je Name mit ihrer Art (ihre Schlüssel gelten in jedem Editor; die Aktion steht im Zustand des jeweiligen): für Zähler
 * und Sprünge über alle. Eine Art kann mehrere haben (Widersprüche: Zeitmarken und Text gegen Angabe).
 */
const registry = new Map<string, { kind: SuggestKind; key: PluginKey<SuggestState<Located>> }>();

/** Offene Vorschläge aller Arten in Textreihenfolge */
export function openSuggestions(state: EditorState): (Located & { kind: SuggestKind })[] {
  const out: (Located & { kind: SuggestKind })[] = [];
  for (const { kind, key } of registry.values()) for (const h of key.getState(state)?.open ?? []) out.push({ ...h, kind });
  return out.sort((a, b) => a.from - b.from || a.to - b.to);
}

/** Zahl der offenen Vorschläge je Art */
export function suggestionCounts(state: EditorState): Record<SuggestKind, number> {
  const counts: Record<SuggestKind, number> = { ref: 0, time: 0, place: 0, state: 0, pov: 0, split: 0, conflict: 0 };
  for (const { kind, key } of registry.values()) counts[kind] += key.getState(state)?.open.length ?? 0;
  return counts;
}

/** Offener Vorschlag am Cursor (auch direkt dahinter, nach einem Klick ans Wortende) */
const atCursor = <H extends Located>(state: EditorState, open: H[]): H | undefined => {
  const { empty, head } = state.selection;
  return empty ? open.find((h) => h.from <= head && head <= h.to) : undefined;
};

/**
 * Die Stelle, zu der zuletzt gesprungen wurde (Alt+↓/↑, Zähler): Sie zeigt sich auch, wenn ihre Farbe beim ruhigen Schreiben ausgeblendet
 * ist (PF 57, Klasse `rt-revealed`), bis der Cursor sie verlässt.
 */
const revealKey = new PluginKey<Located | null>('suggestReveal');

const revealed = (state: EditorState, hit: Located): boolean => {
  const r = revealKey.getState(state);
  return !!r && r.from < hit.to && hit.from < r.to;
};

/** Zum nächsten (`dir` 1) bzw. vorigen (-1) offenen Vorschlag springen (nur der Art `only`); liefert, ob es einen gab */
export function jumpToSuggestion(view: EditorView, dir: 1 | -1, only?: SuggestKind): boolean {
  const all = openSuggestions(view.state).filter((h) => !only || h.kind === only);
  const head = view.state.selection.head;
  const target = dir === 1 ? (all.find((h) => h.from > head) ?? all[0]) : ([...all].reverse().find((h) => h.to < head) ?? all.at(-1));
  if (!target) return false;
  view.dispatch(
    view.state.tr
      .setSelection(TextSelection.create(view.state.doc, target.from))
      .setMeta(revealKey, { from: target.from, to: target.to })
      .scrollIntoView(),
  );
  view.focus();
  return true;
}

/** Den Vorschlag am Cursor annehmen bzw. ignorieren (gleich welcher Art); liefert, ob es einen gab */
export function actAtCursor(state: EditorState, act: SuggestAct): boolean {
  for (const { key } of registry.values()) {
    const s = key.getState(state);
    const hit = s && atCursor(state, s.open);
    if (hit) {
      s.run(act, hit);
      return true;
    }
  }
  return false;
}

function buttons<H extends Located, L extends SuggestLive<H>>(spec: SuggestSpec<H, L>, hit: H, live: { current: L }, shown = false): Decoration {
  const labels = spec.labels?.(hit, live.current) ?? live.current.labels;
  const other = spec.other?.(hit, live.current) ?? null;
  return Decoration.widget(
    hit.to,
    () => {
      const box = document.createElement('span');
      box.className = `rt-suggest-acts ${spec.kind}${shown ? ' revealed' : ''}`;
      box.contentEditable = 'false';
      const add = (act: SuggestAct, text: string, title: string, cls = ''): void => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `rt-suggest-act ${act}${cls}`;
        b.textContent = text;
        b.title = title;
        b.setAttribute('aria-label', `${title}: ${spec.word(hit)}`);
        b.setAttribute('data-testid', `${spec.testId}-${act}`);
        b.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          live.current.act(act, hit);
        });
        box.append(b);
      };
      if (labels.accept !== null) add('accept', '✓', labels.accept);
      if (other) add('other', other.label, other.title, ' text');
      add('ignore', '✕', labels.ignore);
      return box;
    },
    { side: 1, key: `${spec.name}:${hit.from}:${hit.to}:${spec.word(hit)}:${labels.accept ?? ''}:${other?.label ?? ''}${shown ? ':shown' : ''}`, ignoreSelection: true, stopEvent: () => true },
  );
}

function build<H extends Located, L extends SuggestLive<H>>(spec: SuggestSpec<H, L>, state: EditorState, live: { current: L }): SuggestState<H> {
  const l = live.current;
  const run = (act: SuggestAct, hit: H): void => live.current.act(act, hit);
  if (l.on === false) return { hits: [], open: [], deco: DecorationSet.empty, run };
  const hits = spec.hits(state.doc, l, state);
  const open = spec.done ? hits.filter((h) => !spec.done!(h)) : hits;
  const decos = hits.map((h) => {
    const attrs = spec.mark(h, l);
    return Decoration.inline(h.from, h.to, { ...attrs, class: spec.done?.(h) ? attrs.class : `${spec.base ?? 'rt-suggest'} ${attrs.class}` });
  });
  if (l.review) for (const h of open) decos.push(buttons(spec, h, live));
  return { hits, open, deco: DecorationSet.create(state.doc, decos), run };
}

/** Eine Art von Vorschlägen als Erweiterung des Editors. Neu berechnen (Prüfen an/aus, ignorierte Stellen …): `tr.setMeta(key, true)` */
export function suggestExtension<H extends Located, L extends SuggestLive<H>>(spec: SuggestSpec<H, L>, live: { current: L }): Extension {
  registry.set(spec.name, { kind: spec.kind, key: spec.key as unknown as PluginKey<SuggestState<Located>> });
  return Extension.create({
    name: spec.name,
    addProseMirrorPlugins() {
      return [
        new Plugin<SuggestState<H>>({
          key: spec.key,
          state: {
            init: (_, state) => build(spec, state, live),
            apply: (tr, old, _before, state) => (tr.docChanged || tr.getMeta(spec.key) || spec.refresh?.(tr) ? build(spec, state, live) : old),
          },
          props: {
            decorations(state) {
              const s = spec.key.getState(state)!;
              const hit = atCursor(state, s.open);
              const shown = !!hit && revealed(state, hit);
              const extra = shown ? [Decoration.inline(hit.from, hit.to, { class: 'rt-revealed' })] : [];
              if (live.current.review) return extra.length ? s.deco.add(state.doc, extra) : s.deco;
              return hit ? s.deco.add(state.doc, [...extra, buttons(spec, hit, live, shown)]) : s.deco;
            },
          },
        }),
      ];
    },
  });
}

/**
 * Tastenkürzel über alle Arten: Alt+Enter nimmt an, Alt+Rücktaste ignoriert (am Cursor), Alt+↓/↑ springt weiter bzw. zurück. Dazu die
 * Stelle, zu der gesprungen wurde (`revealKey`).
 */
export const suggestKeys = Extension.create({
  name: 'suggestKeys',
  addProseMirrorPlugins() {
    return [
      new Plugin<Located | null>({
        key: revealKey,
        state: {
          init: () => null,
          apply: (tr, old, _before, state) => {
            const meta = tr.getMeta(revealKey) as Located | undefined;
            const r = meta ?? (old && { from: tr.mapping.map(old.from, -1), to: tr.mapping.map(old.to, 1) });
            const head = state.selection.head;
            return r && r.from <= head && head <= r.to ? r : null;
          },
        },
      }),
    ];
  },
  addKeyboardShortcuts() {
    return {
      'Alt-Enter': ({ editor }) => actAtCursor(editor.state, 'accept'),
      'Alt-Backspace': ({ editor }) => actAtCursor(editor.state, 'ignore'),
      'Alt-ArrowDown': ({ editor }) => jumpToSuggestion(editor.view, 1),
      'Alt-ArrowUp': ({ editor }) => jumpToSuggestion(editor.view, -1),
    };
  },
});
